/**
 * Sign in.
 *
 * The whole screen is one 380px panel on the page ground: a wordmark, two fields, one
 * primary button. It is the only screen in the console with no sidebar, no top bar and no
 * data, and it should look like that on purpose — an operator reaching this at 2 AM has
 * either been signed out or is opening NightDesk for the first time, and neither of them
 * wants a landing page.
 *
 * Four things here are deliberate and should survive any redesign:
 *
 *  1. THE ERROR NEVER SAYS WHICH HALF WAS WRONG. The server already returns one identical
 *     401 for unknown email, wrong password, disabled account and platform-staff account
 *     (src/api/auth.ts documents why). This screen renders that sentence verbatim and adds
 *     nothing — a helpful "we don't know that address" would undo the anti-enumeration
 *     work on the other side of the wire.
 *
 *  2. NOTHING SENSITIVE IS REMEMBERED. No "remember me", no stored email, no password
 *     manager hints beyond the standard autocomplete tokens. The session is a HttpOnly
 *     cookie the console cannot read, which is the point of it.
 *
 *  3. VALIDATION IS INLINE AND LATE. Fields validate on blur and on submit, never on the
 *     first keystroke — an error that appears while somebody is still typing their address
 *     is a screen arguing with them.
 *
 *  4. THE PROMISE IS THE PAGE, NOT A FOOTNOTE. "Your line is being answered right now,
 *     whether or not anyone is signed in here" is the one thing somebody locked out at 2 AM
 *     needs to read. It used to sit under the card in mid-grey as fine print — the best
 *     sentence in the product, set as a caption. It is now the largest text on the screen
 *     and the card sits beside it, which is also the only honest ranking: the console is
 *     the secondary object here and the answered line is the primary one.
 *
 * The composition is two columns on a desk and one column stacked on a phone, promise
 * first in both. There is no shadow on the card — this is a resting panel, not a floating
 * one, and the rest of the console builds its structure out of hairlines. A login screen
 * with a drop-shadowed card is a login screen from a different product.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FormEvent, ReactElement } from 'react';

import { ApiError, api } from '../lib/api.ts';
import { qk } from '../lib/useApi.ts';
import type { MeResponse } from '../lib/api.ts';
import { Button, Callout, Field, Icon } from '../components/index.ts';

type FieldErrors = { email?: string; password?: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(email: string, password: string): FieldErrors {
  const errors: FieldErrors = {};
  const trimmed = email.trim();
  if (!trimmed) errors.email = 'Enter the email address you sign in with.';
  else if (!EMAIL_RE.test(trimmed)) errors.email = 'That does not look like an email address.';
  if (!password) errors.password = 'Enter your password.';
  return errors;
}

export function Login(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const client = useQueryClient();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState<{ email: boolean; password: boolean }>({ email: false, password: false });
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  /** Where the operator was headed before the session ran out. */
  const next = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('next');
    // Only same-site paths. An open redirect on a login page is how a phishing link gets
    // to look like ours.
    return raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/';
  }, [location.search]);

  useEffect(() => {
    emailRef.current?.focus();
    document.title = 'Sign in · Milestone NightDesk';
  }, []);

  const fieldErrors = validate(email, password);
  const showErrors = { email: submitted || touched.email, password: submitted || touched.password };

  const login = useMutation<MeResponse, ApiError, { email: string; password: string }>({
    mutationFn: (vars) => api.login(vars),
    onSuccess: (me) => {
      // Login returns the whole MeResponse; seeding the cache means the console's first
      // frame is the real console and not a shell waiting on /api/me.
      client.setQueryData(qk.me, me);
      navigate(next, { replace: true });
    },
  });

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setSubmitted(true);
    const errors = validate(email, password);
    if (errors.email || errors.password) return;
    login.mutate({ email: email.trim(), password });
  };

  const serverError = login.error;
  const rateLimited = serverError?.status === 429;

  const emailError = showErrors.email ? fieldErrors.email : undefined;
  const passwordError = showErrors.password ? fieldErrors.password : undefined;

  return (
    <div className="ndl">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      <div className="ndl-inner">
        <section className="ndl-say">
          <div className="ndl-brand">
            <span className="nd-brand-mark" aria-hidden="true">
              N
            </span>
            <span className="ndl-word">
              Milestone <b>NightDesk</b>
            </span>
          </div>

          <h1 className="ndl-lede">
            Your line is being answered right now, whether or not anyone is signed in here.
          </h1>

          <p className="ndl-sub">
            This console is where you read what happened overnight. It is not what picks up the phone — that runs
            whether this page is open, closed, or signed out.
          </p>

          <p className="ndl-note">
            <Icon name="phone" size={15} />
            <span>After-hours operations console</span>
          </p>
        </section>

        <main className="ndl-card">
        <form className="ndl-form" onSubmit={onSubmit} noValidate>
          {serverError ? (
            <div role="alert">
              <Callout tone={rateLimited ? 'warn' : 'danger'} icon="alert-triangle">
                <b>{rateLimited ? 'Too many attempts' : 'Could not sign in'}.</b> {serverError.message}
              </Callout>
            </div>
          ) : null}

          <Field label="Email" htmlFor="login-email" error={emailError}>
            <input
              ref={emailRef}
              id="login-email"
              className="nd-input"
              type="email"
              name="email"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              value={email}
              disabled={login.isPending}
              data-invalid={emailError ? 'true' : undefined}
              aria-invalid={emailError ? true : undefined}
              aria-describedby={emailError ? 'login-email-hint' : undefined}
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            />
          </Field>

          <Field label="Password" htmlFor="login-password" error={passwordError}>
            <span className="ndl-pw">
              <input
                id="login-password"
                className="nd-input"
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                required
                value={password}
                disabled={login.isPending}
                data-invalid={passwordError ? 'true' : undefined}
                aria-invalid={passwordError ? true : undefined}
                aria-describedby={passwordError ? 'login-password-hint' : undefined}
                onChange={(event) => setPassword(event.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, password: true }))}
              />
              <button
                type="button"
                className="nd-iconbtn ndl-eye"
                aria-label={showPassword ? 'Hide the password' : 'Show the password'}
                aria-pressed={showPassword}
                title={showPassword ? 'Hide' : 'Show'}
                onClick={() => setShowPassword((v) => !v)}
              >
                <Icon name="eye" size={15} />
              </button>
            </span>
          </Field>

          <Button variant="primary" size="lg" block type="submit" loading={login.isPending}>
            {login.isPending ? 'Signing in…' : 'Sign in'}
          </Button>

          <p className="nd-hint ndl-center">
            Forgot your password? An owner or admin on your account can reset it from Settings.
          </p>
        </form>
        </main>
      </div>
    </div>
  );
}

export default Login;

// ---------------------------------------------------------------------------------- css

const CSS = `
.ndl {
  min-height: 100%;
  display: flex; align-items: center; justify-content: center;
  padding: var(--nd-s5); background: var(--nd-page);
}

/*
 * Two columns: the promise, then the transaction. Capped at 880px so the sentence sets at
 * a readable measure instead of running the width of a 27" monitor.
 */
.ndl-inner {
  width: 100%; max-width: 880px;
  display: grid; grid-template-columns: minmax(0, 1fr) 360px;
  gap: var(--nd-s6); align-items: center;
}

.ndl-say { display: flex; flex-direction: column; gap: var(--nd-s3); min-width: 0; }

/*
 * THE HERO. 24/32 semibold — the empty-state title rank, which is the largest prose rank
 * the console already owns, so this borrows no new type from anywhere.
 */
.ndl-lede {
  margin: var(--nd-s2) 0 0;
  font: var(--nd-fw-semibold) var(--nd-fs-24) / 32px var(--nd-sans);
  letter-spacing: -0.015em; color: var(--nd-ink); text-wrap: balance;
}

.ndl-sub {
  margin: 0; max-width: 44ch;
  font: var(--nd-fw-regular) var(--nd-fs-14) / 21px var(--nd-sans);
  color: var(--nd-ink-2);
}

.ndl-note {
  margin: var(--nd-s2) 0 0; display: flex; align-items: center; gap: 8px;
  font-size: var(--nd-fs-12); color: var(--nd-ink-3);
}
.ndl-note > svg { flex: 0 0 auto; }

/*
 * A resting panel: hairline, no shadow. The old 5%-alpha shadow made this the one card in
 * the console that floated, on the one screen a buyer sees first.
 */
.ndl-card {
  width: 100%; justify-self: end;
  background: var(--nd-canvas); border: 1px solid var(--nd-line);
  border-radius: var(--nd-r-panel);
  padding: var(--nd-s5) var(--nd-s5) var(--nd-s4);
}

.ndl-brand { display: flex; align-items: center; gap: 9px; }
.ndl-word { font-size: var(--nd-fs-16); letter-spacing: -0.01em; color: var(--nd-ink-2); }
.ndl-word b { font-weight: var(--nd-fw-semibold); color: var(--nd-ink); }

.ndl-form { display: flex; flex-direction: column; gap: var(--nd-s3); }
.ndl-form .nd-callout { margin-bottom: var(--nd-s1); }

.ndl-pw { position: relative; display: block; }
.ndl-pw .nd-input { padding-right: 38px; }
.ndl-eye { position: absolute; right: 3px; top: 3px; }

.ndl-center { text-align: center; margin-top: var(--nd-s1); }

/*
 * Stacked, promise still first. A phone at 2 AM reads the sentence before it reaches the
 * fields, which is the same ranking as the desk layout.
 */
@media (max-width: 820px) {
  .ndl { align-items: flex-start; padding-top: var(--nd-s6); }
  .ndl-inner { grid-template-columns: minmax(0, 1fr); gap: var(--nd-s5); max-width: 420px; }
  .ndl-lede { font-size: var(--nd-fs-20); line-height: 27px; }
  .ndl-sub { max-width: none; }
  .ndl-card { justify-self: stretch; }
}

@media (max-width: 420px) {
  .ndl { padding: var(--nd-s4); padding-top: var(--nd-s5); }
  .ndl-card { padding: var(--nd-s4); }
}
`;
